import React, { useMemo } from 'react'

import { useEffect, useState } from "react";
import { useTheme } from "@hooks/useTheme";
import queryKeys from "@configs/react-query/query-key";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import useSuccessToast from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import Breadcrumb from "@components/UI/Breadcrumb";
import { useMutationData } from "@hooks/useMutationData";
import { formatDateTime, uppercase } from "@utils/common";
import { TabsContainer } from '@components/Tabs';
import JsonViewer from '@components/UI/JsonViewer';
import { TableWithPagination } from '@components/Tables';
import { useSearchInfo } from '@hooks/useSearchInfo';
import { SCAN_TOOL_OPTIONS } from '@constants/scanTool';
import LoadingSpinner from '@components/UI/LoadingSpinner';

const TEST_ID = "work-history-detail";
const tid = (suffix) => `${TEST_ID}-${suffix}`;
const fieldContainerTestId = (name) => tid(`field-${name}-container`);
const fieldLabelTestId = (name) => tid(`field-${name}-label`);
const fieldValueTestId = (name) => tid(`field-${name}-value`);

const TableChangeView = ({
  jsonData,
  hasDiff,
  itemsPerPage = 10,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);

  const rows = useMemo(() => {
    if (!jsonData) return [];

    let parsed;
    try {
      parsed = typeof jsonData === "string" ? JSON.parse(jsonData) : jsonData;
    } catch (e) {
      console.error("Invalid JSON:", e);
      return [];
    }

    if (hasDiff === "Y") {
      // Diff mode: { field: {old, new}, ... }
      return Object.entries(parsed).map(([key, value]) => ({
        field: key,
        old: value?.old ?? "null",
        new: value?.new ?? "null",
      }));
    } else {
      // Non-diff mode: { field: value, ... }
      return Object.entries(parsed).map(([key, value]) => ({
        field: key,
        new: typeof value === "object" && value !== null
          ? JSON.stringify(value)
          : String(value ?? ""),
      }));
    }
  }, [jsonData, hasDiff]);

  const totalPage = Math.max(1, Math.ceil(rows.length / itemsPerPage));
  const sliceByPage = (rows, page) => rows.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const pagedRows = useMemo(() => sliceByPage(rows, currentPage), [rows, currentPage, itemsPerPage]);


  const columns = useMemo(() => {
    if (hasDiff === "Y") {
      return ["field", "old", "new"];
    } else {
      return ["field", "new"];
    }
  }, [hasDiff])

  const columnConfig = {
    field: {
      style: {
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("work-history.field"),
      customRender: (value, row) => row.field || "",
    },
    old: {
      style: {
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("work-history.old"),
      customRender: (value, row) => row.old || "",
    },
    new: {
      style: {
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("work-history.new"),
      customRender: (value, row) => row.new || "",
    }
  }

  return (
    <div className={`rounded-2xl ${colors.bgPrimary} `} data-testid={tid("content-table-view")}>
      {rows && (
        <TableWithPagination
          columns={columns}
          columnConfig={columnConfig}
          data={pagedRows}
          loading={false}
          totalPages={totalPage}
          totalData={rows.length}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          maxRowsPerPage={itemsPerPage}
          hasCheckbox={false}
          hasRadioButton={false}
          rowKey="field"
          shouldShowGoToPage={true}
          containerStyle={{ borderRadius: "12px" }}
          dataTestId={tid("content-table")}
        />
      )
      }
    </div>
  );
}

const WorkHistoryDetail = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { historySeq } = useParams();
  const [loading, setLoading] = useState(false);
  const [workHistoryData, setworkHistoryData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const { scanToolList, fetchSearchInfo } = useSearchInfo();
  
  const [isProjectDeleted, setIsProjectDeleted] = useState(false);

  const breadcrumbItems = [
    { label: t("work-history.work-history-title"), href: "/work-history" },
    { label: t("work-history.work-history-detail"), href: `/work-history/${historySeq}` },
  ];

  function displayField(value, loading) {
    if (loading) return "";
    const trimmed = value?.trim();
    return (trimmed && trimmed !== "N/A") ? trimmed : "---";
  }

  const isValidUrl = (value) => {
    if (!value) return false;
    const trimmed = value.trim();

    // Internal URL: starts with a single slash (e.g., /projects/285/sbom/574)
    // External URL: starts with http:// or https://
    return /^\/(?!\/)/.test(trimmed) || /^https?:\/\//i.test(trimmed);
  };
  
  const handleClick = (e) => {
    if (disableLocation) {
      e.preventDefault();
      return;
    }

    if (workHistoryData && workHistoryData.identificationNo) {
      navigate(`${workHistoryData.identificationNo}`);
    }
  };

  // API mutations
  const getWorkHistoryData = useMutationData(
    `${queryKeys.getWorkHistoryBySeq}/${historySeq}`,
    { method: "GET" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string" && response?.data) {
          setworkHistoryData(response.data);
          setLoading(false);
        }
      },
      onError: (error) => {
        if (error?.response?.status === 404) {
          navigate("/404", { state: { backTo: "/work-history" } });
        } else {
          useSuccessToast(TOAST.error, t("common.error"), t("work-history.fetch-error"));
        }
        console.error("Error getting work history data:", error);
        setLoading(false);
      },
    },
  );

  const extractProjectSeq = (location) => {
    if (!location) return null;

    const match = location.match(/\/projects\/(\d+)(\/|$)/);
    return match ? Number(match[1]) : null;
  };

  // Hook
  useEffect(() => {
    fetchSearchInfo();
  }, []);

  useEffect(() => {
    if (historySeq) {
      setLoading(true);
      getWorkHistoryData.mutate();
    }
  }, [historySeq]);

  const projectSeq = useMemo(() => {
    return extractProjectSeq(workHistoryData?.identificationNo);
  }, [workHistoryData?.identificationNo]);

  const contentTabs = useMemo(() => {
    return [
      {
        label: t("work-history.table-view"),
        content: <TableChangeView jsonData={workHistoryData?.content} hasDiff={workHistoryData?.hasDiffFlag} />
      },
      {
        label: t("work-history.json-view"),
        content: <JsonViewer jsonData={workHistoryData?.content} />
      }
    ];
  }, [workHistoryData, t]);

  const checkProjectExists = useMutationData(
    projectSeq ? `/projects/${projectSeq}/exists` : null,
    { method: "GET" },
    {
      onSuccess: (response) => {
        const isExisted = response?.data;
        setIsProjectDeleted(!isExisted);
      },
      onError: () => {
        setIsProjectDeleted(true);
      },
    }
  );

  useEffect(() => {
    if (!projectSeq) return;
    checkProjectExists.mutate();
  }, [projectSeq]);

  const disableLocation = useMemo(() => {
    if (!workHistoryData?.entityTypeName) return false;
    
    const hasFossid = scanToolList.some(t => t.value === SCAN_TOOL_OPTIONS.fossid.key);
    const hasClarity = scanToolList.some(t => t.value === SCAN_TOOL_OPTIONS.clarity.key);
    const hasSbom = scanToolList.some(t => t.value === SCAN_TOOL_OPTIONS.sbom.key);

    let disableByTool = false;

    switch (workHistoryData.entityTypeName) {
      case "SourceCodeVerification":
        disableByTool = !hasFossid;
        break;
      case "BinaryVerification":
        disableByTool = !hasClarity;
        break;
      case "SbomVerification":
        disableByTool = !hasSbom;
        break;
      default:
        disableByTool = false;
    }

    return disableByTool || isProjectDeleted;
  }, [scanToolList, workHistoryData, isProjectDeleted]);

  if (loading) {
    return (
      <LoadingSpinner />
    );
  }

  return (
    <div className={`${colors.bgSecondary}`} data-testid={tid("page")}>
      {/* Header */}
      <div className="mb-6" data-testid={tid("header")}>
        <Breadcrumb items={breadcrumbItems} data-testid={tid("breadcrumb")} />
      </div>

      <div className="flex items-center justify-between pb-5">
        <h1
          className={`text-2xl truncate max-w-[500px] font-bold ${colors.textPrimary}`}
          title={workHistoryData?.workTypeCdName || ""}
        >
          {workHistoryData?.workTypeCdName || "LOADING..."}
        </h1>

      </div>

      <div data-testid={tid("info")} className={`rounded-2xl border ${colors.borderPrimary} ${colors.bgPrimary}`}>
        <div className="flex flex-col lg:flex-row">
          <div className={`w-full p-6 m`}>
            <h2 className={`text-lg font-bold ${colors.textPrimary} mb-4`}>
              {uppercase(t("work-history.work-history-info"))}
            </h2>

            <div className="space-y-2 px-4 sm:px-0">
              {/* row 1 */}
              <div className="grid grid-cols-1 gap-x-4 gap-y-4 pt-3">
                <div data-testid={fieldContainerTestId("work-type")}>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("work-type")}>
                    {t("work-history.work-type")}
                  </label>
                  <p
                    className={`text-sm truncate ${colors.textPrimary} h-8 flex items-center ${!workHistoryData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                    data-testid={fieldValueTestId("work-type")}
                  >
                    {displayField(workHistoryData?.workTypeCdName, loading)}
                  </p>
                </div>
              </div>

              {/* row 2 */}
              <div className={`grid grid-cols-3 gap-x-4 gap-y-4 border-t ${colors.borderSection} border-dashed pt-3`}>
                <div data-testid={fieldContainerTestId("entity-id")}>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("entity-id")}>
                    {t("work-history.entity-id")}
                  </label>
                  <p
                    className={`text-sm truncate ${colors.textPrimary} h-8 flex items-center ${loading || !workHistoryData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                    data-testid={fieldValueTestId("entity-id")}
                  >
                    {displayField(workHistoryData?.entityId, loading)}
                  </p>
                </div>
                <div data-testid={fieldContainerTestId("location")}>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("location")}>
                    {t("work-history.entity-type")}
                  </label>
                  <p
                    className={`text-sm truncate ${colors.textPrimary} h-8 flex items-center ${loading || !workHistoryData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                  >
                    {displayField(workHistoryData?.entityTypeName, loading)}
                  </p>
                </div>

                <div>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("work-history.request-uri")}
                  </label>
                  <p
                    className={`text-sm truncate ${colors.textPrimary} h-8 flex items-center ${loading || !workHistoryData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                  >
                    {displayField(workHistoryData?.requestUri, loading)}
                  </p>
                </div>
              </div>

              {/* row 3 */}
              <div className={`grid grid-cols-3 gap-x-4 gap-y-4 border-t ${colors.borderSection} border-dashed pt-3`}>
                <div>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("work-history.worker-id")}
                  </label>
                  <p
                    className={`text-sm truncate ${colors.textPrimary} h-8 flex items-center ${loading || !workHistoryData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                  >
                    {displayField(workHistoryData?.workerId, loading)}
                  </p>
                </div>
                <div>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("work-history.worked-at")}
                  </label>
                  <p
                    className={`text-sm truncate ${colors.textPrimary} h-8 flex items-center ${loading || !workHistoryData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}`}
                  >
                    {displayField(formatDateTime(workHistoryData?.workedAt), loading)}
                  </p>
                </div>
                <div>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`}>
                    {t("work-history.location")}
                  </label>
                  <p
                    className={`text-sm truncate ${colors.textPrimary} h-8 flex items-center 
                      ${loading || !workHistoryData ? `animate-pulse rounded ${colors.bgSkeleton}` : ""}
                      ${disableLocation ? "opacity-50" : ""}
                    `}
                  >
                    {workHistoryData?.identificationNo ? (
                      isValidUrl(workHistoryData.identificationNo) ? (
                        disableLocation ? (
                          <span className="cursor-not-allowed">{workHistoryData.identificationNo}</span>
                        ) : (
                          <span
                            data-testid={tid("location-link")}
                            className={`${colors.textComparisonLink} ${colors.hoverTextComparisonLink} underline cursor-pointer`}
                            onClick={handleClick}

                          >
                            {workHistoryData.identificationNo}
                          </span>
                        )
                      ) : (
                        <span>{workHistoryData.identificationNo}</span>
                      )
                    ) : (
                      "---"
                    )}
                  </p>
                </div>
              </div>

              {/* row 4 */}
              <div className="grid grid-cols-1 gap-x-4 pt-3" data-testid={fieldContainerTestId("description")}>
                <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("description")}>
                  {t("work-history.description")}
                </label>

                <div
                  className={` p-4 mb-4 h-[6rem] rounded-2xl border ${colors.bgSecondary} ${colors.borderPrimary} overflow-y-auto`}
                >
                  <p className={`text-sm font-normal ${colors.textMessage} whitespace-pre-wrap`} data-testid={fieldValueTestId("description")}>
                    {workHistoryData?.description || ""}
                  </p>
                </div>
              </div>

              <div className={`rounded-2xl border ${colors.borderPrimary} ${colors.bgPrimary}`}>
                <div className="flex flex-col lg:flex-row">
                  <div className={`w-full p-6 m`}>
                    <h2 className={`text-lg font-bold ${colors.textPrimary} mb-3`}>
                      {uppercase(t("work-history.content"))}
                    </h2>

                    <div data-testid={tid("content-tabs")}>
                      <TabsContainer
                        tabs={contentTabs}
                        defaultActiveTab={activeTab}
                        onTabChange={setActiveTab}
                        dataTestId={tid("content-tabs-container")}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default WorkHistoryDetail